/* eslint-disable @typescript-eslint/no-empty-function */
import type { OnInit } from '@angular/core';
import { Component, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { SkipNavComponent } from '@core-mkt/shared/components/skip-nav/skip-nav/skip-nav.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faCaretDown,
  faCaretUp,
  faChevronDown,
  faChevronUp,
  faQuestionCircle,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { cloneDeep } from 'lodash-es';
import type { Subscription } from 'rxjs';
import type { Header, HeaderNavLink } from './header';
import { headerData } from './header-data';

@Component({
  selector: 'aarp-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [FaIconComponent, SkipNavComponent, LazyImgDirective, QueryParamDirective],
})
export class HeaderComponent implements OnInit {
  // @HostBinding('class.sticky-header')
  headerSticky = false;
  showHeader = true;
  faQuestionCircle = faQuestionCircle;
  faUserCircle = faUserCircle;
  faCaretDown = faCaretDown;
  faCaretUp = faCaretUp;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  showMenu = false;
  resolverData: PageDataResponse;
  hideNav = false;
  headerData: Header = headerData;
  subscription: Subscription;
  currentUrl: string;
  isScrolled = false;
  showMore = false;
  activeDropdown: string | null = null;
  private lastCaretClickTime = 0;

  constructor(private route: ActivatedRoute) {
    if (this.route.snapshot) {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData as PageDataResponse);
    }
  }

  ngOnInit() {
    if (this.resolverData?.craftData?.headerData) {
      this.hideNav = this.resolverData ? this.resolverData?.craftData?.headerData?.hideNav : false;
      this.headerSticky = this.resolverData ? this.resolverData?.craftData?.headerData?.isNavSticky : false;
    }
    if (this.hideNav) {
      this.showHeader = false;
    }
    this.currentUrl = `/${this.route.snapshot.url.join('/')}`;
  }

  get mainHeaderLinks(): HeaderNavLink[] {
    return this.headerData.aarpMainLinks;
  }

  get mainHeaderLinksStickyNavMobile(): HeaderNavLink[] {
    return this.headerData.aarpMainLinks.length >= 2
      ? this.headerData.aarpMainLinks.slice(0, 2)
      : this.headerData.aarpMainLinks.slice();
  }

  get mainHeaderLinksStickyNavMobileMore(): HeaderNavLink[] {
    const links = this.headerData.aarpMainLinks.length > 2 ? this.headerData.aarpMainLinks.slice(2) : [];
    return links.flatMap((link) =>
      link.isDropDown && link.ddList?.length
        ? link.ddList.map((item) => ({ ...item, isDropDown: false as const }))
        : [link],
    );
  }

  get loginLink(): HeaderNavLink {
    return this.headerData.aarpLoginLink;
  }

  get helpLink(): HeaderNavLink {
    return this.headerData.aarpHelpLink;
  }

  get registerLink(): HeaderNavLink {
    return this.headerData.aarpRegisterLink;
  }

  isDropdownActive(link: HeaderNavLink): boolean {
    return link.linkUrl === this.currentUrl || (link.ddList ?? []).some((item) => item.linkUrl === this.currentUrl);
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
    if (!this.showMenu) {
      this.activeDropdown = null;
    }
  }

  toggleMobileDropdown(link: HeaderNavLink, event: Event): void {
    if (this.activeDropdown === link.linkUrl) {
      // Second tap on title/whitespace: allow the browser to follow the link normally
      return;
    } else {
      // First tap: prevent navigation and expand the dropdown
      event.preventDefault();
      this.activeDropdown = link.linkUrl;
    }
  }

  toggleMobileDropdownCaret(link: HeaderNavLink): void {
    const now = Date.now();
    if (now - this.lastCaretClickTime < 300) {
      return;
    }
    this.lastCaretClickTime = now;
    this.activeDropdown = this.activeDropdown === link.linkUrl ? null : link.linkUrl;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isScrolled = scrollY > 80;
  }
}
